import React, { useCallback, useState, useEffect } from 'react';
import { Modal, Box, Typography, IconButton } from '@mui/material';
import { ReactComponent as CloseIcon } from 'app/assets/Icons/Close.svg';
import { ReactComponent as FileUpload } from 'app/assets/Icons/FileUpload.svg';
import { ReactComponent as CheckIcon } from 'app/assets/Icons/Check.svg';
import { useDropzone } from 'react-dropzone';
import { Button } from '@zurich-primitives/button';
import { useDispatch } from 'react-redux';
import { setSelectedLanguage } from 'app/redux';
import styles from './UploadDocumentModal.module.scss';
import languages from 'data/mock/languages.json';
import classnames from 'classnames';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  acceptedFileTypes: { [key: string]: string[] };
  showLanguageSelector?: boolean;
  title?: string;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  acceptedFileTypes,
  showLanguageSelector = false,
  title = 'Upload Files'
}) => {
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>('english');

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
    if (fileRejections.length > 0) {
      setErrorMessage('File type not allowed.');
      setUploadedFiles([]);
    } else {
      setUploadedFiles(acceptedFiles);
      setErrorMessage(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: acceptedFileTypes,
    onDropRejected: () => setErrorMessage('File type not allowed.') // Handle rejected files
  });

  const handleDeleteFile = () => {
    setUploadedFiles([]);
    setErrorMessage(null);
  };

  const handleUpload = () => {
    if (uploadedFiles.length > 0) {
      onUpload(uploadedFiles[0]);
      onClose();
    }
  };

  useEffect(() => {
    dispatch(setSelectedLanguage(language));
  }, [language, dispatch]);

  // Reset modal state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setUploadedFiles([]);
      setErrorMessage(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setUploadedFiles([]);
    setErrorMessage(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="upload-modal-title"
    >
      <Box className={styles.modalContent}>
        <IconButton
          aria-label="Close modal"
          onClick={handleClose}
          className={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          id="upload-modal-title"
          variant="h6"
          className={styles.title}
        >
          {title}
        </Typography>

        {showLanguageSelector && (
          <div className={styles.languageSelectorContainer}>
            <Typography className={styles.description} align="center">
              Select your preferred output language
            </Typography>

            <select
              id="languageSelect"
              className={styles.selectElement}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {languages.map((language) => (
                <option key={language.name} value={language.name.toLowerCase()}>
                  {language.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div
          {...getRootProps()}
          className={`${styles.fileInputContainer} ${
            isDragActive ? styles.dragActive : ''
          }`}
        >
          <input {...getInputProps()} />
          {uploadedFiles.length === 0 ? (
            <>
              <Button variant="secondary" className={styles.browseButton}>
                <Button.Icon>
                  <span>
                    <FileUpload />
                  </span>
                </Button.Icon>
                Browse File
              </Button>
              <Typography className={styles.fileInputLabel}>
                Browse a file or drag it here to upload
              </Typography>
              {errorMessage && (
                <Typography className={styles.errorMessage}>
                  {errorMessage}
                </Typography>
              )}
            </>
          ) : (
            <div className={styles.uploadedFile}>
              <div className={styles.fileDetails}>
                <CheckIcon className={styles.checkIcon} />
                <p
                  className={classnames({
                    [styles.filename]: uploadedFiles[0].name.length > 20
                  })}
                >
                  {uploadedFiles[0].name.length > 20
                    ? `${uploadedFiles[0].name.slice(0, 20)}...`
                    : uploadedFiles[0].name}
                  {uploadedFiles[0].name.length > 20 && (
                    <span className={styles.tooltip}>
                      {uploadedFiles[0].name}
                    </span>
                  )}
                </p>
              </div>

              <Button
                variant="secondary"
                onClick={handleDeleteFile}
                className={styles.deleteButton}
                aria-label="Delete uploaded file"
              >
                <CloseIcon />
              </Button>
            </div>
          )}
        </div>

        <div className={styles.buttonContainer}>
          <Button onClick={handleClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="primary"
            disabled={uploadedFiles.length === 0}
          >
            Upload
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default UploadDocumentModal;
